import { Link } from "react-router-dom";
import { ArrowRight, Check, Sparkles, TrendingUp, Crown } from "lucide-react";

const TIERS = [
  {
    name: "Ignite",
    icon: Sparkles,
    tagline: "Build the Business.",
    price: "₹25,000",
    priceNote: "+ GST · Annual",
    desc:
      "Designed for owners who are ready to plug into a serious network, learn the D32 playbook and build their first meaningful referral relationships.",
    perks: [
      "First 90-Day Momentum Plan + member training",
      "Fortnightly in-person growth meetings",
      "Monthly Maximiser sessions with guest speakers",
      "D32 member directory + city network access",
      "IGNITE Growth Guarantee*",
    ],
    accent: "bg-white border-gray-200",
    badge: "Entry",
  },
  {
    name: "Impact",
    icon: TrendingUp,
    tagline: "Grow the Business.",
    price: "₹40,000",
    priceNote: "+ GST · Annual",
    desc:
      "For established owners ready for serious referral velocity, deeper peer learning, and a seat at the Mastermind Forum.",
    perks: [
      "Everything in Ignite",
      "Ladies / Mens / Sundowner curated events",
      "Online personal leadership workshops",
      "Mastermind Forum (one seat per profession)",
      "Priority matching with high-value referrers",
    ],
    accent: "bg-navy text-white border-navy",
    badge: "Most Popular",
    featured: true,
  },
  {
    name: "Influence",
    icon: Crown,
    tagline: "Lead the Business.",
    price: "₹80,000",
    priceNote: "+ GST · Annual",
    desc:
      "An invite-only mastermind for ₹8 Cr+ business owners who want to shortcut years of trial and error alongside serious peers.",
    perks: [
      "Private peer mastermind (₹8 Cr+ owners)",
      "Curated 1:1 introductions across India",
      "Strategy sessions with national speakers",
      "Pan-India network access",
      "Founder retreats and off-sites",
    ],
    accent: "bg-white border-gray-200",
    badge: "Invite-only",
  },
];

export default function Membership() {
  return (
    <div data-testid="membership-page" className="bg-white">
      {/* HERO */}
      <section className="bg-navy text-white py-24 lg:py-32">
        <div className="container-narrow">
          <div className="overline text-gold mb-4">The Pathway to Growth · India</div>
          <h1 className="text-4xl sm:text-6xl font-display font-semibold leading-[1.05] max-w-4xl">
            One membership. <span className="italic text-gold">Three tiers of growth.</span>
          </h1>
          <p className="mt-8 max-w-2xl text-lg text-gray-300 leading-relaxed">
            Wherever you are in your journey, starting out, scaling up, or already at the top of your market, there's a D32 India room built for where you stand today.
          </p>
        </div>
      </section>

      {/* TIERS */}
      <section className="py-24 lg:py-28">
        <div className="container-narrow">
          <div className="grid lg:grid-cols-3 gap-6">
            {TIERS.map((tier) => {
              const Icon = tier.icon;
              return (
                <div
                  key={tier.name}
                  data-testid={`membership-tier-${tier.name.toLowerCase()}`}
                  className={`relative p-8 lg:p-10 rounded-2xl border transition-all duration-300 ${
                    tier.featured
                      ? "bg-navy text-white border-navy lg:scale-[1.03] shadow-2xl"
                      : "bg-white border-gray-200 hover:-translate-y-1 hover:border-navy/40"
                  }`}
                >
                  <div className="flex items-center justify-between mb-6">
                    <div
                      className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                        tier.featured ? "bg-gold text-white" : "bg-navy text-gold"
                      }`}
                    >
                      <Icon size={22} />
                    </div>
                    <span
                      className={`text-[10px] uppercase tracking-widest font-semibold px-3 py-1 rounded-full ${
                        tier.featured
                          ? "bg-gold text-white"
                          : "bg-gray-100 text-gray-600"
                      }`}
                    >
                      {tier.badge}
                    </span>
                  </div>
                  <h3 className={`text-3xl font-display font-semibold ${tier.featured ? "text-white" : "text-navy"}`}>
                    {tier.name}
                  </h3>
                  <div className={`mt-1 text-sm italic ${tier.featured ? "text-gold" : "text-gray-500"}`}>
                    {tier.tagline}
                  </div>

                  <div className="mt-6">
                    <div className={`font-display text-4xl ${tier.featured ? "text-white" : "text-navy"}`}>
                      {tier.price}
                    </div>
                    <div className={`text-xs mt-1 ${tier.featured ? "text-gray-300" : "text-gray-500"}`}>
                      {tier.priceNote}
                    </div>
                  </div>

                  <p className={`mt-5 text-sm leading-relaxed ${tier.featured ? "text-gray-300" : "text-gray-600"}`}>
                    {tier.desc}
                  </p>
                  <ul className="mt-6 space-y-3">
                    {tier.perks.map((p) => (
                      <li key={p} className="flex items-start gap-2 text-sm">
                        <Check size={16} className={`mt-0.5 flex-shrink-0 ${tier.featured ? "text-gold" : "text-navy"}`} />
                        <span className={tier.featured ? "text-gray-200" : "text-gray-700"}>{p}</span>
                      </li>
                    ))}
                  </ul>

                  <div className="mt-8 flex flex-col gap-3">
                    <a
                      href="mailto:mayuri@franglobal.com?subject=Book%20a%20call%20-%20D32%20India%20Membership"
                      data-testid={`buy-${tier.name.toLowerCase()}`}
                      className={`w-full inline-flex items-center justify-center gap-2 font-semibold py-3 rounded-full transition ${
                        tier.featured
                          ? "bg-gold text-white hover:bg-[#33c5f5]"
                          : "bg-navy text-white hover:bg-navy-deep"
                      }`}
                    >
                      Book a call
                    </a>
                    <Link
                      to="/visit-a-circle"
                      className={`text-center text-sm font-semibold ${tier.featured ? "text-gold" : "text-navy"} hover:underline`}
                    >
                      Or visit a region first →
                    </Link>
                  </div>
                </div>
              );
            })}
          </div>
          <p className="mt-8 text-xs text-gray-500 italic max-w-3xl">
            *IGNITE Growth Guarantee: eligible Ignite members who fully participate and don't generate business equal to 2× their membership investment in the first year may qualify for a complimentary renewal period. Full conditions apply.
          </p>
        </div>
      </section>

      {/* MEMBER TESTIMONIALS */}
      <section className="bg-cream py-24 lg:py-28">
        <div className="container-narrow">
          <div className="overline text-gold mb-4">Members. In their own words.</div>
          <h2 className="text-3xl sm:text-4xl font-display font-semibold text-navy leading-tight max-w-2xl">
            The right tier at the right time changes everything.
          </h2>
          <div className="mt-12 grid md:grid-cols-2 gap-6">
            {[
              {
                quote:
                  "The Impact tier paid for itself in my first quarter. But what I didn't expect was the Mastermind, my monthly pressure valve as an owner.",
                name: "Aarti Desai",
                role: "Founder, Desai Wealth Partners · Ahmedabad · Impact member",
              },
              {
                quote:
                  "Ignite taught me how referrals actually work. The 90-day plan, the 1:1s, the discipline. Membership is the best growth spend I've made.",
                name: "Vikram Singh",
                role: "Founder, Tricity Insurance Brokers · Chandigarh Tricity · Ignite member",
              },
            ].map((t) => (
              <div key={t.name} className="bg-white border border-gray-200 rounded-2xl p-8">
                <div className="text-gold text-3xl font-display leading-none">"</div>
                <p className="mt-2 text-gray-700 leading-relaxed">{t.quote}</p>
                <div className="mt-6 text-sm">
                  <div className="font-semibold text-navy">{t.name}</div>
                  <div className="text-gray-500">{t.role}</div>
                </div>
              </div>
            ))}
          </div>

          <div
            className="mt-12 aspect-[16/6] rounded-3xl bg-cover bg-center shadow-lg"
            style={{
              backgroundImage:
                "url(https://images.unsplash.com/photo-1560523159-4a9692d222f9?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600)",
            }}
          />
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="bg-white py-24 lg:py-28">
        <div className="container-narrow">
          <div className="overline text-gold mb-4">How joining works</div>
          <h2 className="text-3xl sm:text-4xl font-display font-semibold text-navy leading-tight max-w-2xl">
            Four steps from a first meeting to full member.
          </h2>
          <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              ["01", "Visit a region", "Come to one D32 India meeting as our guest. Meet local business owners, see the format, ask anything."],
              ["02", "Apply", "Submit your application. We check that your profession has an open seat in that region."],
              ["03", "Onboarding", "90-day Momentum Plan, member training, referral partner intros, directory access."],
              ["04", "Grow", "Consistently show up, give first, and let the network work for your business."],
            ].map(([n, title, body]) => (
              <div key={n} className="bg-white border border-gray-200 rounded-2xl p-7">
                <div className="font-display text-gold text-3xl">{n}</div>
                <h3 className="mt-3 font-display text-lg text-navy">{title}</h3>
                <p className="mt-2 text-sm text-gray-600 leading-relaxed">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24">
        <div className="container-narrow">
          <div className="bg-navy text-white rounded-3xl p-10 lg:p-16 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
            <div className="max-w-xl">
              <h3 className="text-3xl sm:text-4xl font-display font-semibold leading-tight">
                Not sure which tier yet?
              </h3>
              <p className="mt-3 text-gray-300">
                Come to one meeting first. We'll help you pick the right room.
              </p>
            </div>
            <Link
              to="/visit-a-circle"
              className="inline-flex items-center gap-2 bg-gold text-white font-semibold px-7 py-4 rounded-full hover:-translate-y-0.5 transition-all"
            >
              Visit a Region <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
